import { useId, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { CREDENTIALS, PARENT } from '@padhai/shared';
import type { ParentMe } from '@padhai/shared';
import { ApiError, apiPost } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { Button, Card, ErrorNote, Field, cx, inputClass } from '../components/ui.js';
import { BrandLogo } from '../components/Brand.js';

type Mode = 'student' | 'parent' | 'staff';

/**
 * Sign-in. Students use school code, roll number and PIN; staff use email and
 * password (spec section 10 — students have no email by design). A parent uses
 * the school code, the child's roll number and the parent PIN from the teacher,
 * and lands on the parent view rather than signing in as anyone.
 *
 * Built for a low-end Android in Chrome: one column, 44px targets, a numeric
 * keyboard for the PIN, and nothing that needs a second tap to read.
 */
export function Login() {
  const location = useLocation();
  const [mode, setMode] = useState<Mode>(
    (location.state as { as?: string } | null)?.as === 'parent' ? 'parent' : 'student',
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { user, signInStudent, signInStaff } = useAuth();
  const navigate = useNavigate();

  const ids = {
    schoolCode: useId(),
    rollNo: useId(),
    pin: useId(),
    email: useId(),
    password: useId(),
  };

  const from = (location.state as { from?: string } | null)?.from;
  const pinLength = mode === 'parent' ? PARENT.pinLength : CREDENTIALS.pinLength;

  // Already signed in: the sign-in form has nothing to offer, and showing it
  // invites a second account on a shared device without signing out the first.
  if (user) return <Navigate to={from ?? '/'} replace />;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const data = new FormData(event.currentTarget);
    try {
      if (mode === 'parent') {
        await apiPost<ParentMe>('/api/v1/auth/parent/login', {
          schoolCode: String(data.get('schoolCode') ?? ''),
          rollNo: String(data.get('rollNo') ?? ''),
          pin: String(data.get('pin') ?? ''),
        });
        navigate('/parent', { replace: true });
        return;
      }
      if (mode === 'student') {
        await signInStudent({
          schoolCode: String(data.get('schoolCode') ?? ''),
          rollNo: String(data.get('rollNo') ?? ''),
          pin: String(data.get('pin') ?? ''),
        });
      } else {
        await signInStaff({
          email: String(data.get('email') ?? ''),
          password: String(data.get('password') ?? ''),
        });
      }
      navigate(from ?? '/', { replace: true });
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not sign in. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rise mx-auto grid max-w-5xl items-center gap-10 py-2 sm:py-6 lg:grid-cols-[1.1fr_1fr] lg:gap-16">
      {/*
       * The welcome half, on a wide screen: the logo, what Padhai is for, and
       * three things a student does here. A phone gets the logo above the form
       * instead, and nothing that pushes the form below the fold.
       */}
      <section className="hidden lg:block" aria-label="About Padhai">
        <BrandLogo className="w-[25rem]" />
        <h2 className="mt-10 text-[1.65rem] font-semibold leading-tight">
          Your own tutor for the Class 10 board exam.
        </h2>
        <ul className="mt-6 space-y-4">
          {WELCOME_POINTS.map((point) => (
            <li key={point.title} className="flex gap-3">
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-full bg-sun text-on-sun"
              >
                {point.icon}
              </span>
              <span>
                <span className="block font-medium">{point.title}</span>
                <span className="block text-sm text-ink-soft">{point.body}</span>
              </span>
            </li>
          ))}
        </ul>
      </section>

      <div className="mx-auto w-full max-w-sm">
        <BrandLogo className="mx-auto mb-7 w-56 lg:hidden" />
        <div className="text-center lg:text-left">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome back</h1>
          <p className="mt-1.5 text-sm text-ink-soft">Sign in to keep learning.</p>
        </div>

        <Card className="mt-6 p-5">
          <div
            className="flex gap-1 rounded-control bg-surface-sunken p-1"
            role="group"
            aria-label="Who is signing in"
          >
            <ModeButton current={mode} value="student" onSelect={setMode}>
              Student
            </ModeButton>
            <ModeButton current={mode} value="parent" onSelect={setMode}>
              Parent
            </ModeButton>
            <ModeButton current={mode} value="staff" onSelect={setMode}>
              Staff
            </ModeButton>
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            {mode !== 'staff' ? (
              <>
                <Field id={ids.schoolCode} label="School code">
                  <input
                    id={ids.schoolCode}
                    name="schoolCode"
                    required
                    autoComplete="organization"
                    autoCapitalize="characters"
                    spellCheck={false}
                    placeholder="e.g. ZPHS-GUDIVADA"
                    className={inputClass}
                  />
                </Field>
                <Field
                  id={ids.rollNo}
                  label={mode === 'parent' ? "Your child's roll number" : 'Roll number'}
                >
                  <input
                    id={ids.rollNo}
                    name="rollNo"
                    required
                    inputMode="numeric"
                    autoComplete="username"
                    placeholder="014"
                    className={inputClass}
                  />
                </Field>
                <Field
                  id={ids.pin}
                  label={mode === 'parent' ? 'Parent PIN' : 'PIN'}
                  hint={`${pinLength} digits`}
                >
                  <input
                    key={mode}
                    id={ids.pin}
                    name="pin"
                    type="password"
                    required
                    inputMode="numeric"
                    pattern={`\\d{${pinLength}}`}
                    maxLength={pinLength}
                    autoComplete="current-password"
                    className={cx(inputClass, 'tracking-[0.4em]')}
                  />
                </Field>
              </>
            ) : (
              <>
                <Field id={ids.email} label="Email">
                  <input
                    id={ids.email}
                    name="email"
                    type="email"
                    required
                    autoComplete="username"
                    spellCheck={false}
                    className={inputClass}
                  />
                </Field>
                <Field id={ids.password} label="Password">
                  <input
                    id={ids.password}
                    name="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    className={inputClass}
                  />
                </Field>
              </>
            )}

            {error ? <ErrorNote>{error}</ErrorNote> : null}

            <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
              {busy ? 'Signing in' : 'Sign in'}
            </Button>
          </form>
        </Card>

        <p className="mt-4 text-center text-sm text-ink-soft">
          {mode === 'student'
            ? 'Forgot your PIN? Ask your teacher to reset it.'
            : mode === 'parent'
              ? "No parent PIN yet? Ask your child's class teacher. It is not your child's PIN."
              : 'Forgot your password? Ask the school admin to reset it.'}
        </p>
      </div>
    </div>
  );
}

const WELCOME_POINTS = [
  {
    title: 'Learn from your own textbook',
    body: 'Ask about any open chapter and get it explained, step by step.',
    icon: '📖',
  },
  {
    title: 'Practice at your level',
    body: 'Short sets that focus on what you have not got yet.',
    icon: '✏️',
  },
  {
    title: 'See what the board asks',
    body: 'Ten years of papers, chapter by chapter.',
    icon: '🎯',
  },
];

function ModeButton({
  current,
  value,
  onSelect,
  children,
}: {
  current: Mode;
  value: Mode;
  onSelect: (mode: Mode) => void;
  children: ReactNode;
}) {
  const selected = current === value;
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={() => onSelect(value)}
      className={cx(
        'min-h-10 flex-1 rounded-[0.45rem] px-3 text-sm font-medium transition-colors',
        selected ? 'bg-surface text-ink shadow-card' : 'text-ink-soft hover:text-ink',
      )}
    >
      {children}
    </button>
  );
}
